import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPlus, FaEdit, FaTrash, FaStethoscope, FaHome, FaVideo, FaPhone, FaComments } from 'react-icons/fa';
import { API_BASE_URL, AUTH_TOKEN_KEY, consultationTypes } from '../../utils/constants';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './ManageServicesScreen.css';

const ManageServicesScreen = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    serviceType: '',
    fees: '',
    description: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      
      const response = await fetch(`${API_BASE_URL}api/doctor/services`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setServices(data.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.serviceType || !formData.fees) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const url = editingService 
        ? `${API_BASE_URL}api/doctor/services/${editingService._id}`
        : `${API_BASE_URL}api/doctor/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingService ? 'Service updated successfully' : 'Service added successfully');
        setFormData({ serviceType: '', fees: '', description: '' });
        setShowAddForm(false);
        setEditingService(null);
        fetchServices();
      } else {
        toast.error('Failed to save service');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      serviceType: service.serviceType,
      fees: service.fees.toString(),
      description: service.description || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      
      const response = await fetch(`${API_BASE_URL}api/doctor/services/${serviceId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Service deleted successfully');
        fetchServices();
      } else {
        toast.error('Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'inClinic': return <FaStethoscope />;
      case 'homeVisit': return <FaHome />;
      case 'videoConsultation': return <FaVideo />;
      case 'teleConsultation': return <FaPhone />;
      case 'chatConsultation': return <FaComments />;
      default: return <FaStethoscope />;
    }
  };

  const getServiceLabel = (serviceType) => {
    const type = consultationTypes.find(t => t.key === serviceType);
    return type ? type.label : serviceType;
  };

  if (loading) {
    return <LoadingSpinner message="Loading services..." />;
  }

  return (
    <div className="manage-services-screen">
      <div className="manage-services-container">
        <div className="services-header">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          <FaArrowLeft /> Back
        </button>
        <h2>Manage Services</h2>
        <button 
          onClick={() => setShowAddForm(true)} 
          className="add-button"
        >
          <FaPlus /> Add Service
        </button>
      </div>

      {showAddForm && (
        <div className="add-form-overlay">
          <div className="add-form">
            <h3>{editingService ? 'Edit Service' : 'Add New Service'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Service Type *</label>
                <select
                  value={formData.serviceType}
                  onChange={(e) => setFormData({...formData, serviceType: e.target.value})}
                  required
                >
                  <option value="">Select Service Type</option>
                  {consultationTypes.map(type => (
                    <option key={type.key} value={type.key}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Consultation Fee (₹) *</label>
                <input
                  type="number"
                  value={formData.fees}
                  onChange={(e) => setFormData({...formData, fees: e.target.value})}
                  placeholder="Enter consultation fee"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description"
                  rows="3"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowAddForm(false);
                  setEditingService(null);
                  setFormData({ serviceType: '', fees: '', description: '' });
                }} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingService ? 'Update' : 'Add'} Service
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="services-list">
        {services.length === 0 ? (
          <div className="empty-state">
            <FaStethoscope className="empty-icon" />
            <h3>No services added yet</h3>
            <p>Add your consultation services to start accepting appointments</p>
          </div>
        ) : (
          services.map((service) => (
            <div key={service._id} className="service-card">
              <div className="service-info">
                <div className="service-icon">
                  {getServiceIcon(service.serviceType)}
                </div>
                <div className="service-details">
                  <h4>{getServiceLabel(service.serviceType)}</h4>
                  <p className="service-fee">₹{service.fees}</p>
                  {service.description && (
                    <p className="service-description">{service.description}</p>
                  )}
                </div>
              </div>
              <div className="service-actions">
                <button 
                  onClick={() => handleEdit(service)}
                  className="edit-btn"
                >
                  <FaEdit />
                </button>
                <button 
                  onClick={() => handleDelete(service._id)}
                  className="delete-btn"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  );
};

export default ManageServicesScreen;